import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTenant } from "../../context/TenantContext";
import { SELLER_DETAILS_URL } from "../../features/UrlHelper";
import "./VOPPage.scss";

/**
 * Obchodné podmienky (R6, F6).
 *
 * PREČO SA CELÉ PREPÍSALI:
 *   Predošlé znenie výslovne hovorilo, že cez platformu sa neobjednáva a že
 *   zmluva vzniká mimo nej. Odkedy si zákazník vie objednať cez web, je to
 *   nepravda — a nepravdivé VOP sú horšie než žiadne.
 *
 * DVE STRANY, NIE JEDNA:
 *   Zákazník uzatvára zmluvu s PREVÁDZKOU, nie s INVERom. INVER dodáva len
 *   softvér. Preto sú podmienky rozdelené: časť o nákupe hovorí o prevádzke
 *   (jej údaje sa ťahajú z `/api/seller-details`), časť o platforme o INVERe.
 *   Keby to bolo zmiešané, zákazník by nevedel, komu reklamovať obed.
 */
// Pri zmene textov sa mení aj `VERZIA_PODMIENOK` v backend/services/terms.js.
// Keď sa rozídu, zákazník odsúhlasí iný dátum, než aký vidí.
const PLATNE_OD = "3. 10. 2026";

const platformaZUrl = () =>
  window.location.origin.replace(/^https?:\/\//i, "").replace(/\/+$/, "");

function VOPPage() {
  const { branding, tenant, mode } = useTenant();
  // rozvozy.sk/vop -- ziadna prevadzka; cita to skor majitel z predajnej stranky
  const naKoreni = mode === "root";
  const platforma = platformaZUrl();
  const [predavajuci, setPredavajuci] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let zrusene = false;
    axios
      .get(SELLER_DETAILS_URL)
      .then(({ data }) => { if (!zrusene) setPredavajuci(data); })
      // Bez údajov sa text nerozsype — ukáže sa aspoň názov prevádzky
      // a odkaz na kontakt.
      .catch(() => { if (!zrusene) setPredavajuci(null); });
    return () => { zrusene = true; };
  }, []);

  const nazovPrevadzky =
    predavajuci?.legal_name || predavajuci?.nazov || branding?.display_name || tenant?.name || "prevádzka";

  return (
    <main className="vop-page">
      <div className="vop-page__container">
        <h1 className="vop-page__title">Obchodné podmienky</h1>
        <p className="vop-page__meta">platné od: {PLATNE_OD}</p>

        {naKoreni && (
          <p className="vop-page__perex vop-page__perex--poznamka">
            Toto sú obchodné podmienky pre stravníkov, ktorí si objednávajú jedlo u prevádzok
            v systéme Rozvozy. Ak ste majiteľ prevádzky a zaujíma vás používanie systému, pozrite si
            stránku <Link to="/pre-prevadzky">Rozvozy pre prevádzky</Link>.
          </p>
        )}

        <p className="vop-page__perex">
          Tieto podmienky upravujú objednávanie jedla u prevádzky{" "}
          {naKoreni ? "zapojenej do systému Rozvozy" : <strong>{nazovPrevadzky}</strong>}{" "}
          cez stránku {platforma} a používanie
          samotnej stránky. Sú to dve rôzne veci a majú aj dvoch rôznych
          zodpovedných — nižšie je napísané, kto je kto.
        </p>

        {/* ── 1. Kto je kto ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Kto je predávajúci a kto prevádzkuje stránku</h2>

          <h3 className="vop-page__subheading">Predávajúci (jedlo si objednávate od neho)</h3>
          {predavajuci?.kompletne ? (
            <div className="vop-page__details-block">
              <p className="vop-page__details-line">{predavajuci.legal_name}</p>
              <p className="vop-page__details-line">sídlo: {predavajuci.legal_address}</p>
              <p className="vop-page__details-line">IČO: {predavajuci.ico}</p>
              {predavajuci.dic && <p className="vop-page__details-line">DIČ: {predavajuci.dic}</p>}
              <p className="vop-page__details-line">
                {predavajuci.ic_dph
                  ? `IČ DPH: ${predavajuci.ic_dph}`
                  : "IČ DPH: predávajúci nie je platiteľom DPH"}
              </p>
              {predavajuci.legal_registration && (
                <p className="vop-page__details-line">
                  zápis v registri: {predavajuci.legal_registration}
                </p>
              )}
              {predavajuci.email && (
                <p className="vop-page__details-line">e-mail: {predavajuci.email}</p>
              )}
              {predavajuci.phone && (
                <p className="vop-page__details-line">telefón: {predavajuci.phone}</p>
              )}
              <p className="vop-page__details-line">(ďalej len „predávajúci“).</p>
            </div>
          ) : (
            <p>
              Identifikačné údaje predávajúceho nájdete v sekcii{" "}
              <Link to="/kontakt">Kontakt</Link>.
            </p>
          )}
          <p>
            S predávajúcim uzatvárate kúpnu zmluvu. Jemu patrí jedlo, cena aj
            zodpovednosť zaň a s ním riešite všetko, čo sa objednávky týka.
          </p>

          <h3 className="vop-page__subheading">Prevádzkovateľ stránky (dodávateľ softvéru)</h3>
          <div className="vop-page__details-block">
            <p className="vop-page__details-line">INVER spol. s r.o.</p>
            <p className="vop-page__details-line">sídlo: Hutnícka 1, 040 01 Košice, Slovenská republika</p>
            <p className="vop-page__details-line">IČO: 31665462</p>
            <p className="vop-page__details-line">DIČ: 2020490934</p>
            <p className="vop-page__details-line">IČ DPH: SK2020490934</p>
            <p className="vop-page__details-line">zápis v registri: OR MS Košice, Oddiel: S.r.o., Vložka: 2533/V</p>
            <p className="vop-page__details-line">e-mail: admin@rozvozy.sk</p>
            <p className="vop-page__details-line">(ďalej len „prevádzkovateľ stránky“).</p>
          </div>
          <p>
            Prevádzkovateľ stránky poskytuje predávajúcemu softvér na prijímanie
            objednávok. Nie je predávajúcim, jedlo nepripravuje, nerozváža ani
            nepredáva a nie je stranou kúpnej zmluvy.
          </p>
        </section>

        {/* ── 2. Objednávka a vznik zmluvy ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Objednávka a vznik zmluvy</h2>
          <p>
            Objednávku môžete zadať po prihlásení do zákazníckeho účtu. Pred odoslaním
            vidíte súhrn — zoznam jedál, počty a celkovú cenu.
          </p>
          <p>
            Odoslaním objednávky navrhujete predávajúcemu uzatvorenie kúpnej zmluvy.
            <strong> Zmluva vzniká prijatím objednávky v systéme predávajúceho</strong> —
            v tom okamihu ju uvidíte medzi svojimi objednávkami v stave „Prijatá“.
            Potvrdenie vám následne pošleme e-mailom; je to doklad o už uzatvorenej
            zmluve, nie podmienka jej vzniku. Ak by vám e-mail z technických príčin
            neprišiel, na platnosti objednávky to nič nemení.
          </p>
          <p>
            Objednávať sa dá len na dni a do času, ktoré stránka ponúka. Čas, dokedy
            prevádzka objednávky na daný deň prijíma, je uvedený pri objednávaní;
            po jeho uplynutí sa na ten deň objednať nedá.
          </p>
          <p>
            Predávajúci môže objednávku odmietnuť, najmä ak sa jedlo minulo alebo ho
            nevie pripraviť. V takom prípade vás o tom upovedomí a zaplatená suma sa
            nevyžaduje — platí sa až pri prevzatí.
          </p>
        </section>

        {/* ── 3. Ceny a platba ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Ceny a platba</h2>
          <p>
            Ceny uvedené pri jedlách sú konečné, vrátane DPH, ak je predávajúci jej
            platiteľom. Záväzná je cena zobrazená v súhrne objednávky v čase jej
            odoslania.
          </p>
          <p>
            S niektorými zákazníkmi má predávajúci dohodnutý vlastný cenník. Ak sa vás
            to týka, po prihlásení vidíte ceny podľa neho.
          </p>
          <p>
            <strong>Cez stránku sa neplatí.</strong> Platí sa až pri prevzatí jedla —
            v hotovosti, kartou, na faktúru alebo na účet (podľa toho, čo máte
            s predávajúcim dohodnuté). Stránka neprijíma platby kartou ani neuchováva
            platobné údaje.
          </p>
          <p>
            Za rozvoz sa neúčtuje osobitný poplatok a nie je stanovená minimálna výška
            objednávky, ak predávajúci neurčí inak a neoznámi to vopred.
          </p>
        </section>

        {/* ── 4. Prevzatie ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Dodanie a vyzdvihnutie</h2>
          <p>
            Jedlo buď dovezieme na vašu adresu, alebo si ho vyzdvihnete na prevádzke —
            podľa toho, čo je pri vašej adrese dohodnuté a čo ste pri objednávke
            zvolili.
          </p>
          <p>
            Donáška nie je nárok. Rozvoz na konkrétnu adresu musí predávajúci najprv
            povoliť. Kým sa tak nestane, objednávku si vyzdvihnete na prevádzke;
            o donášku môžete požiadať priamo pri objednávaní a predávajúci sa vám ozve.
          </p>
          <p>
            Čas doručenia je orientačný a závisí od počtu objednávok a dopravy. Ak
            jedlo neprevezmete a nedá sa vám dovolať, predávajúci objednávku zruší
            a môže požadovať úhradu nákladov, ktoré mu tým vznikli.
          </p>
        </section>

        {/* ── 5. Zmena a zrušenie ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Zmena a zrušenie objednávky</h2>
          <p>
            Objednávku môžete zrušiť sami v sekcii <em>Moje objednávky</em>, kým je
            v stave „Prijatá“ a kým sa na daný deň ešte dá objednávať. Potom už jedlo
            ide do prípravy a zrušiť sa dá len dohodou s predávajúcim.
          </p>
          <p>
            Zrušenie je bezplatné. Ak objednávku zruší predávajúci, dozviete sa to
            e-mailom aj s dôvodom.
          </p>
        </section>

        {/* ── 6. Odstúpenie do 14 dní ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Odstúpenie od zmluvy do 14 dní</h2>
          <p>
            Pri pripravenom jedle <strong>nemáte právo odstúpiť od zmluvy do 14 dní</strong>.
            Vyplýva to z § 19 ods. 1 písm. d) zákona č. 108/2024 Z. z. o ochrane
            spotrebiteľa: právo odstúpiť od zmluvy sa neuplatňuje pri tovare, ktorý
            podlieha rýchlemu zníženiu kvality alebo skaze, ani pri tovare zhotovenom
            podľa osobitných požiadaviek spotrebiteľa.
          </p>
          <p>
            Nemá to vplyv na vaše právo objednávku zrušiť podľa bodu 5 ani na práva
            z vadného plnenia podľa bodu 7.
          </p>
        </section>

        {/* ── 7. Reklamácie ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Reklamácie</h2>
          <p>
            Za kvalitu, zloženie, hmotnosť, alergény, teplotu aj balenie jedla
            zodpovedá predávajúci. Reklamáciu uplatnite u neho, a to bez zbytočného
            odkladu — pri jedle najlepšie hneď pri prevzatí alebo v ten istý deň,
            kým sa dá posúdiť jeho stav.
          </p>
          <p>
            Reklamáciu môžete podať telefonicky, e-mailom alebo osobne na prevádzke
            (kontakty nájdete v sekcii <Link to="/kontakt">Kontakt</Link>). Predávajúci
            vám vydá potvrdenie o jej uplatnení a vybaví ju najneskôr do 30 dní odo
            dňa uplatnenia; o výsledku vás vyrozumie.
          </p>
          <p>
            Predávajúci zodpovedá za vady, ktoré má jedlo pri prevzatí. Ak je vadné,
            máte právo na jeho výmenu alebo na vrátenie zaplatenej sumy; vzhľadom na
            povahu jedla sa oprava neuplatňuje. Pri jedle sa vady prejavia hneď, preto
            ho reklamujte čo najskôr po prevzatí.
          </p>
          <p>
            Ak nie ste spokojní s vybavením reklamácie, máte právo obrátiť sa na
            predávajúceho so žiadosťou o nápravu. Ak ju zamietne alebo na ňu do 30 dní
            neodpovie, môžete podať návrh na alternatívne riešenie sporu. Príslušným
            subjektom je <strong>Slovenská obchodná inšpekcia</strong>, Ústredný
            inšpektorát, Odbor pre medzinárodné vzťahy a alternatívne riešenie
            spotrebiteľských sporov, Bajkalská 21/A, 827 99 Bratislava 27,{" "}
            <a href="https://www.soi.sk/sk/alternativne-riesenie-spotrebitelskych-sporov.soi"
               target="_blank" rel="noopener noreferrer">
              www.soi.sk
            </a>. Zvoliť si môžete aj iný subjekt zapísaný v zozname vedenom
            Ministerstvom hospodárstva SR.
          </p>
        </section>

        {/* ── 8. Účet ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Zákaznícky účet</h2>
          <p>
            Na objednávanie potrebujete účet. Pri registrácii uvádzate pravdivé údaje
            a udržiavate ich aktuálne — podľa nich vás vodič hľadá a volá vám.
          </p>
          <p>
            Heslo k účtu nikomu neposkytujte. Za objednávky zadané cez váš účet
            zodpovedáte vy; ak máte podozrenie na zneužitie, heslo si zmeňte v profile
            a dajte vedieť predávajúcemu.
          </p>
          <p>
            Účet môžete kedykoľvek zrušiť — stačí o to požiadať predávajúceho.
            Predávajúci môže účet zrušiť alebo obmedziť tomu, kto ho opakovane
            zneužíva, najmä zadávaním objednávok, ktoré si neprevezme.
          </p>
        </section>

        {/* ── 9. Zodpovednosť stránky ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Zodpovednosť prevádzkovateľa stránky</h2>
          <p>
            Prevádzkovateľ stránky zodpovedá za jej chod, nie za obsah objednávky.
            Nezodpovedá za kvalitu jedla, ceny, dostupnosť ani za doručenie — to je
            vecou predávajúceho.
          </p>
          <p>
            Prevádzkovateľ stránky sa snaží o jej nepretržitú dostupnosť, nemôže ju
            však zaručiť; stránka môže byť krátkodobo nedostupná pre údržbu alebo
            poruchu. Keď sa objednávka nedá zadať cez web, dá sa zadať telefonicky.
          </p>
          <p>
            Obsah stránky (texty, grafika, dizajn, logá) je chránený autorským právom.
            Stránku nepoužívajte spôsobom, ktorý by narušil jej chod alebo bezpečnosť.
          </p>
        </section>

        {/* ── 10. Osobné údaje ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Osobné údaje</h2>
          <p>
            Ako sa nakladá s vašimi údajmi, kto je ich prevádzkovateľom a aké máte
            práva, nájdete v{" "}
            <Link to="/ochrana-osobnych-udajov">Zásadách ochrany osobných údajov</Link>.
          </p>
        </section>

        {/* ── 11. Záver ── */}
        <section className="vop-page__section">
          <h2 className="vop-page__heading">Záverečné ustanovenia</h2>
          <p>
            Vzťahy neupravené týmito podmienkami sa riadia právnym poriadkom Slovenskej
            republiky, najmä Občianskym zákonníkom a zákonom č. 108/2024 Z. z. o ochrane
            spotrebiteľa a o zmene a doplnení niektorých zákonov.
          </p>
          <p>
            Podmienky sa môžu zmeniť. Nové znenie zverejníme na tejto stránke a pri
            najbližšom prihlásení vás požiadame o jeho odsúhlasenie. Na už potvrdené
            objednávky sa vzťahuje znenie platné v čase ich odoslania.
          </p>
          <p className="vop-page__meta">Toto znenie je platné od {PLATNE_OD}.</p>
        </section>
      </div>
    </main>
  );
}

export default VOPPage;
